import { mungeSDPPlay } from "./mungeSDP";
import stopPlay from "./stopPlay";
import * as WebRTCPlayActions from "orion-components/GlobalData/WowzaWebRTC/WebRTCPlay/actionTypes";

// Utilities
let repeaterRetryCount = 0;
let sessionId = "[empty]";
let getOfferQueue = []; // Queue for pending getOffer requests
let isProcessingGetOffer = false; // Track if a getOffer is in progress

const getStreamInfo = (playSettings) => {
	return {
		applicationName: playSettings.applicationName,
		streamName: playSettings.streamName,
		sessionId: sessionId
	};
};

const getUserData = () => {
	return { param1: "value1" };
};

// PeerConnection Functions
const peerConnectionSetRemoteDescriptionSuccess = (description, playSettings, websocket, peerConnection, callbacks) => {
	peerConnection
		.setLocalDescription(description)
		.then(() => {
			websocket.send(
				'{"direction":"play", "command":"sendResponse", "streamInfo":' +
					JSON.stringify(getStreamInfo(playSettings)) +
					', "sdp":' +
					JSON.stringify(description) +
					', "userData":' +
					JSON.stringify(getUserData(playSettings)) +
					"}"
			);
		})
		.catch((error) => {
			const newError = { message: "Peer connection failed", ...error };
			peerConnectionOnError(newError, callbacks);
		});
};

const peerConnectionOnError = (error, callbacks) => {
	console.log("peerConnectionOnError");
	console.log(error);
	if (callbacks.onError) callbacks.onError({ message: "PeerConnection Error: " + error.message });
};

// Websocket Functions
const websocketOnOpen = (playSettings, websocket, callbacks, reduxStore) => {
	let peerConnection;

	try {
		peerConnection = new RTCPeerConnection();

		peerConnection.ontrack = (event) => {
			if (callbacks.onPeerConnectionOnTrack) callbacks.onPeerConnectionOnTrack(event);
		};

		peerConnection.onconnectionstatechange = (event) => {
			if (event.currentTarget.connectionState === "connected") {
				if (callbacks.onConnectionStateChange) callbacks.onConnectionStateChange({ connected: true });
			} else {
				if (callbacks.onConnectionStateChange) callbacks.onConnectionStateChange({ connected: false });
			}
		};

		websocket.addEventListener("message", (event) => {
			websocketOnMessage(event, playSettings, peerConnection, websocket, callbacks, reduxStore);
		});

		websocketSendPlayGetOffer(playSettings, websocket);
	} catch (e) {
		websocketOnError(e, callbacks, reduxStore, playSettings);
	}
	if (callbacks.onSetPeerConnection) callbacks.onSetPeerConnection({ peerConnection: peerConnection });
};

const websocketOnMessage = (event, playSettings, peerConnection, websocket, callbacks, reduxStore) => {
	const msgJSON = JSON.parse(event.data);
	const msgStatus = Number(msgJSON["status"]);

	if (msgStatus === 514 || msgStatus === 504) {
		// repeater stream not ready
		repeaterRetryCount++;

		if (repeaterRetryCount < 10) {
			setTimeout(() => {
				// set isProcessingGetOffer to false so next getOffer request can be sent
				isProcessingGetOffer = false;
				websocketSendPlayGetOffer(playSettings, websocket);
			}, 1000);
		} else {
			websocketOnError(
				{ message: "Live stream repeater timeout: " + playSettings.streamName },
				callbacks,
				reduxStore,
				playSettings
			);
			stopPlay(peerConnection, websocket, callbacks);
		}
	} else if (msgStatus !== 200) {
		websocketOnError({ message: msgJSON["statusDescription"] }, callbacks, reduxStore, playSettings);
		stopPlay(peerConnection, websocket, callbacks);
	} else {
		const streamInfoResponse = msgJSON["streamInfo"];
		if (streamInfoResponse !== undefined) {
			sessionId = streamInfoResponse.sessionId;
		}

		if (msgJSON["sdp"] != null) {
			msgJSON.sdp.sdp = mungeSDPPlay(msgJSON.sdp.sdp);

			peerConnection
				.setRemoteDescription(new RTCSessionDescription(msgJSON.sdp))
				.then(() => {
					peerConnection
						.createAnswer()
						.then((description) => {
							peerConnectionSetRemoteDescriptionSuccess(
								description,
								playSettings,
								websocket,
								peerConnection,
								callbacks
							);
						})
						.catch((err) => {
							peerConnectionOnError(err, callbacks);
							// Release queue on error
							isProcessingGetOffer = false;
							processNextGetOffer();
						});
				})
				.catch((err) => {
					peerConnectionOnError(err, callbacks);
					// Release queue on error
					isProcessingGetOffer = false;
					processNextGetOffer();
				});
		}
		const iceCandidates = msgJSON["iceCandidates"];
		if (iceCandidates != null) {
			for (const index in iceCandidates) {
				peerConnection.addIceCandidate(new RTCIceCandidate(iceCandidates[index]));
			}
			// Release queue after adding ICE candidates
			isProcessingGetOffer = false;
			processNextGetOffer();
		}
	}
};

const websocketOnError = (error, callbacks, reduxStore, playSettings) => {
	console.log("Websocket Error");
	console.log(error);
	reduxStore.dispatch({ type: WebRTCPlayActions.STREAM_FAILED, data: playSettings });
	if (callbacks.onError) callbacks.onError({ websocketError: error.message });
	// Release queue on websocket error
	isProcessingGetOffer = false;
	processNextGetOffer();
};

const websocketSendPlayGetOffer = (playSettings, websocket) => {
	// Queue the getOffer request
	getOfferQueue.push({ playSettings, websocket });
	processNextGetOffer();
};

const processNextGetOffer = () => {
	if (isProcessingGetOffer || getOfferQueue.length === 0) {
		return;
	}
	isProcessingGetOffer = true;
	const { playSettings, websocket } = getOfferQueue.shift();
	try {
		websocket.send(
			'{"direction":"play", "command":"getOffer", "streamInfo":' +
				JSON.stringify(getStreamInfo(playSettings)) +
				', "userData":' +
				JSON.stringify(getUserData(playSettings)) +
				"}"
		);
	} catch (error) {
		console.error("Failed to process queued getOffer:", error);
		isProcessingGetOffer = false;
		processNextGetOffer(); // Try next in queue
	}
};

// startPlay
// callbacks:
// - onError({message:''})
// - onPeerConnectionOnTrack({event:obj})
// - onConnectionStateChange({connected:boolean})
// - onSetPeerConnection({peerConnection:obj})
// - onSetWebsocket({websocket:obj})
const startPlay = (playSettings, websocket, callbacks, reduxStore) => {
	try {
		reduxStore.dispatch({ type: WebRTCPlayActions.STREAM_FAILED, data: playSettings });
		if (websocket == null) {
			websocket = new WebSocket(playSettings.signalingURL);
		}
		if (websocket != null) {
			repeaterRetryCount = 0;

			websocket.binaryType = "arraybuffer";

			websocket.addEventListener("open", () => {
				websocketOnOpen(playSettings, websocket, callbacks, reduxStore);
			});
			websocket.addEventListener("error", (error) => {
				websocketOnError(error, callbacks, reduxStore, playSettings);
			});

			if (callbacks.onSetWebsocket) callbacks.onSetWebsocket({ websocket: websocket });
		}
	} catch (e) {
		if (callbacks.onError) callbacks.onError(e);
	}
};

export default startPlay;
